import { useState, useRef, useEffect, useCallback, memo } from 'react';
import { Search, Shuffle, X, Upload as UploadIcon, ImageIcon, Loader2, Trash2 } from 'lucide-react';
import { uploadIconImage } from '../../utils/iconUpload';
import { icons } from 'lucide-react';
import data from '@emoji-mart/data';
import { init, SearchIndex } from 'emoji-mart';
import {
    ICON_CATEGORIES,
    ICON_COLORS,
    searchIcons,
    getValidCategoryIcons,
} from './lucideIconData';
import IconRenderer from './IconRenderer';
import './UniversalIconPicker.css';

// Initialize emoji-mart data
init({ data });

// Emoji categories (for our custom grid using emoji-mart data)
const EMOJI_CATS = [
    { id: 'frequent',       label: 'Frequently Used', icon: '🕐' },
    { id: 'people',         label: 'People',          icon: '😀' },
    { id: 'nature',         label: 'Animals & Nature', icon: '🌿' },
    { id: 'foods',          label: 'Food & Drink',    icon: '🍕' },
    { id: 'activity',       label: 'Activities',      icon: '⚽' },
    { id: 'places',         label: 'Travel & Places', icon: '✈️' },
    { id: 'objects',        label: 'Objects',          icon: '💡' },
    { id: 'symbols',        label: 'Symbols',         icon: '💜' },
    { id: 'flags',          label: 'Flags',            icon: '🏁' },
];

// Get emojis for a category from emoji-mart data
function getEmojisForCategory(catId) {
    const cat = data.categories.find(c => c.id === catId);
    if (!cat) return [];
    return cat.emojis.map(emojiId => {
        const emojiData = data.emojis[emojiId];
        if (!emojiData) return null;
        // Get the native emoji from the first skin
        const skin = emojiData.skins?.[0];
        return skin?.native ? { id: emojiId, native: skin.native, name: emojiData.name } : null;
    }).filter(Boolean);
}

// Search emojis using emoji-mart's search index
async function searchEmojis(query) {
    if (!query.trim()) return [];
    const results = await SearchIndex.search(query);
    if (!results) return [];
    return results.map(emoji => {
        const skin = emoji.skins?.[0];
        return skin?.native ? { id: emoji.id, native: skin.native, name: emoji.name } : null;
    }).filter(Boolean);
}

// Normalize any icon value to an IconObject
function normalizeIcon(icon) {
    if (!icon) return null;
    if (typeof icon === 'string') return { type: 'emoji', value: icon, color: null };
    if (icon.type && icon.value) return icon;
    return null;
}

// Random emoji for shuffle
function getRandomEmoji() {
    const cats = data.categories.filter(c => c.id !== 'frequent');
    const randomCat = cats[Math.floor(Math.random() * cats.length)];
    const emojiIds = randomCat.emojis;
    const randomId = emojiIds[Math.floor(Math.random() * emojiIds.length)];
    const emojiData = data.emojis[randomId];
    const native = emojiData?.skins?.[0]?.native;
    return native || '🎯';
}

/**
 * UniversalIconPicker — Notion-style 3-tab icon picker.
 * 
 * Props:
 *   value       IconObject | string | null
 *   onChange     (iconObject) => void
 *   onRemove    () => void (optional)
 *   triggerSize  "sm" | "md" | "lg" (default: "md")
 *   renderTrigger  (props) => ReactNode (optional custom trigger)
 *   popoverClassName string (optional extra class for popover)
 */
function UniversalIconPicker({
    value,
    onChange,
    onRemove,
    triggerSize = 'md',
    renderTrigger,
    popoverClassName = '',
}) {
    const [open, setOpen] = useState(false);
    const [tab, setTab] = useState('emoji'); // 'emoji' | 'icons' | 'upload'
    const [search, setSearch] = useState('');
    const [emojiCategory, setEmojiCategory] = useState('people');
    const [iconCategory, setIconCategory] = useState('common');
    const [iconColor, setIconColor] = useState(null);
    const [emojiResults, setEmojiResults] = useState([]);
    const [showColorPicker, setShowColorPicker] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState(null);
    const [dragOver, setDragOver] = useState(false);
    const fileInputRef = useRef(null);
    const ref = useRef(null);
    const searchRef = useRef(null);

    const normalized = normalizeIcon(value);

    // Close on outside click
    useEffect(() => {
        if (!open) return;
        const handler = (e) => {
            if (ref.current && !ref.current.contains(e.target)) {
                setOpen(false);
                setSearch('');
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [open]);

    // Focus search on open
    useEffect(() => {
        if (open && searchRef.current) {
            setTimeout(() => searchRef.current?.focus(), 50);
        }
    }, [open, tab]);

    // Emoji search debounce
    useEffect(() => {
        if (tab !== 'emoji' || !search.trim()) {
            setEmojiResults([]);
            return;
        }
        const timer = setTimeout(async () => {
            const results = await searchEmojis(search);
            setEmojiResults(results);
        }, 150);
        return () => clearTimeout(timer);
    }, [search, tab]);

    const handleSelectEmoji = useCallback((native) => {
        onChange({ type: 'emoji', value: native, color: null });
        setOpen(false);
        setSearch('');
    }, [onChange]);

    const handleSelectIcon = useCallback((iconName) => {
        onChange({ type: 'lucide', value: iconName, color: iconColor });
        setOpen(false);
        setSearch('');
    }, [onChange, iconColor]);

    const handleShuffle = useCallback(() => {
        const emoji = getRandomEmoji();
        onChange({ type: 'emoji', value: emoji, color: null });
    }, [onChange]);

    const handleRemove = useCallback(() => {
        onRemove?.();
        setOpen(false);
    }, [onRemove]);

    const handleToggle = useCallback((e) => {
        e.stopPropagation();
        e.preventDefault();
        setOpen(prev => !prev);
        setSearch('');
        setUploadError(null);
    }, []);

    // Upload handler
    const processFile = useCallback(async (file) => {
        if (!file) return;
        setUploadError(null);
        setUploading(true);
        try {
            const url = await uploadIconImage(file);
            onChange({ type: 'upload', value: url, color: null });
            setOpen(false);
        } catch (err) {
            setUploadError(err.message);
        } finally {
            setUploading(false);
        }
    }, [onChange]);

    const handleDrop = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragOver(false);
        const file = e.dataTransfer?.files?.[0];
        if (file) processFile(file);
    }, [processFile]);

    const handleDragOver = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragOver(true);
    }, []);

    const handleDragLeave = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragOver(false);
    }, []);

    const handleFileInput = useCallback((e) => {
        const file = e.target?.files?.[0];
        if (file) processFile(file);
        // Reset so same file can be re-selected
        if (e.target) e.target.value = '';
    }, [processFile]);

    // Icon search results
    const iconSearchResults = tab === 'icons' && search.trim()
        ? searchIcons(search)
        : [];

    // Emoji grid content
    const emojiGridContent = search.trim()
        ? emojiResults
        : getEmojisForCategory(emojiCategory);

    // Icon grid content
    const currentIconCategory = ICON_CATEGORIES.find(c => c.key === iconCategory);
    const iconGridContent = search.trim()
        ? iconSearchResults
        : currentIconCategory ? getValidCategoryIcons(currentIconCategory) : [];

    // Trigger element
    const triggerEl = renderTrigger ? (
        renderTrigger({ onClick: handleToggle, icon: normalized })
    ) : (
        <button
            type="button"
            className={`btn btn-ghost btn-sm btn-icon uip-trigger uip-trigger--${triggerSize}`}
            onClick={handleToggle}
            title="Change icon"
        >
            <IconRenderer icon={normalized} size={triggerSize === 'sm' ? 16 : triggerSize === 'lg' ? 32 : 20} />
        </button>
    );

    return (
        <div className="uip" ref={ref}>
            {triggerEl}

            {open && (
                <div className={`uip-popover ${popoverClassName}`}>
                    {/* Tab bar */}
                    <div className="uip-tabs">
                        <button
                            className={`uip-tab ${tab === 'emoji' ? 'uip-tab--active' : ''}`}
                            onClick={() => { setTab('emoji'); setSearch(''); }}
                        >
                            Emoji
                        </button>
                        <button
                            className={`uip-tab ${tab === 'icons' ? 'uip-tab--active' : ''}`}
                            onClick={() => { setTab('icons'); setSearch(''); }}
                        >
                            Icons
                        </button>
                        <button
                            className={`uip-tab ${tab === 'upload' ? 'uip-tab--active' : ''}`}
                            onClick={() => { setTab('upload'); setSearch(''); setUploadError(null); }}
                        >
                            Upload
                        </button>
                        {onRemove && (
                            <button
                                className="uip-tab uip-tab--remove"
                                onClick={handleRemove}
                            >
                                Remove
                            </button>
                        )}
                    </div>

                    {/* Search bar — hidden on upload tab */}
                    {tab !== 'upload' && (
                    <div className="uip-search">
                        <Search size={14} className="uip-search__icon" />
                        <input
                            ref={searchRef}
                            className="uip-search__input"
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            placeholder={tab === 'emoji' ? 'Search emojis...' : 'Search icons...'}
                        />
                        {tab === 'emoji' && (
                            <button
                                className="btn btn-ghost btn-sm uip-search__action"
                                onClick={handleShuffle}
                                title="Random emoji"
                            >
                                <Shuffle size={14} />
                            </button>
                        )}
                        {tab === 'icons' && (
                            <div className="uip-color-trigger-wrap">
                                <button
                                    className="btn btn-ghost btn-sm uip-search__action uip-color-trigger"
                                    onClick={() => setShowColorPicker(!showColorPicker)}
                                    title="Icon color"
                                >
                                    <span
                                        className="uip-color-dot"
                                        style={{ background: iconColor || 'var(--text-secondary)' }}
                                    />
                                </button>
                                {showColorPicker && (
                                    <div className="uip-color-popover">
                                        {ICON_COLORS.map(c => (
                                            <button
                                                key={c.label}
                                                className={`uip-color-swatch ${iconColor === c.value ? 'uip-color-swatch--active' : ''}`}
                                                style={{ background: c.value || 'var(--text-secondary)' }}
                                                onClick={() => { setIconColor(c.value); setShowColorPicker(false); }}
                                                title={c.label}
                                            />
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                        {search && (
                            <button className="btn btn-ghost btn-sm uip-search__action" onClick={() => setSearch('')}>
                                <X size={14} />
                            </button>
                        )}
                    </div>
                    )}

                    {/* ===== EMOJI TAB ===== */}
                    {tab === 'emoji' && (
                        <>
                            {!search.trim() && (
                                <div className="uip-categories">
                                    {EMOJI_CATS.filter(c => c.id !== 'frequent').map(cat => (
                                        <button
                                            key={cat.id}
                                            className={`uip-cat-btn ${emojiCategory === cat.id ? 'uip-cat-btn--active' : ''}`}
                                            onClick={() => setEmojiCategory(cat.id)}
                                            title={cat.label}
                                        >
                                            {cat.icon}
                                        </button>
                                    ))}
                                </div>
                            )}
                            <div className="uip-grid-wrapper">
                                {search.trim() && emojiGridContent.length === 0 && (
                                    <div className="uip-empty">No emojis found</div>
                                )}
                                {!search.trim() && (
                                    <div className="uip-section-label">
                                        {EMOJI_CATS.find(c => c.id === emojiCategory)?.label || 'Emojis'}
                                    </div>
                                )}
                                <div className="uip-grid uip-grid--emoji">
                                    {emojiGridContent.map(emoji => (
                                        <button
                                            key={emoji.id}
                                            type="button"
                                            className={`uip-grid-item ${normalized?.type === 'emoji' && normalized?.value === emoji.native ? 'uip-grid-item--selected' : ''}`}
                                            onClick={() => handleSelectEmoji(emoji.native)}
                                            title={emoji.name}
                                        >
                                            {emoji.native}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </>
                    )}

                    {/* ===== ICONS TAB ===== */}
                    {tab === 'icons' && (
                        <>
                            {!search.trim() && (
                                <div className="uip-categories uip-categories--icons">
                                    {ICON_CATEGORIES.map(cat => (
                                        <button
                                            key={cat.key}
                                            className={`uip-cat-btn btn btn-ghost btn-sm uip-cat-btn--text ${iconCategory === cat.key ? 'uip-cat-btn--active' : ''}`}
                                            onClick={() => setIconCategory(cat.key)}
                                        >
                                            {cat.label}
                                        </button>
                                    ))}
                                </div>
                            )}
                            <div className="uip-grid-wrapper">
                                {search.trim() && iconGridContent.length === 0 && (
                                    <div className="uip-empty">No icons found</div>
                                )}
                                {!search.trim() && (
                                    <div className="uip-section-label">
                                        {currentIconCategory?.label || 'Icons'}
                                    </div>
                                )}
                                <div className="uip-grid uip-grid--icons">
                                    {iconGridContent.map(iconName => {
                                        const LucideIcon = icons[iconName];
                                        if (!LucideIcon) return null;
                                        const isSelected = normalized?.type === 'lucide' && normalized?.value === iconName;
                                        return (
                                            <button
                                                key={iconName}
                                                type="button"
                                                className={`uip-grid-item uip-grid-item--icon ${isSelected ? 'uip-grid-item--selected' : ''}`}
                                                onClick={() => handleSelectIcon(iconName)}
                                                title={iconName.replace(/([A-Z])/g, ' $1').trim()}
                                            >
                                                <LucideIcon
                                                    size={20}
                                                    color={iconColor || 'currentColor'}
                                                    strokeWidth={1.75}
                                                />
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        </>
                    )}

                    {/* ===== UPLOAD TAB ===== */}
                    {tab === 'upload' && (
                        <div className="uip-upload">
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml"
                                className="uip-upload__input"
                                onChange={handleFileInput}
                            />

                            {/* Current uploaded icon preview */}
                            {normalized?.type === 'upload' && normalized?.value && (
                                <div className="uip-upload__current">
                                    <img
                                        src={normalized.value}
                                        alt="Current icon"
                                        className="uip-upload__current-img"
                                    />
                                    <span className="uip-upload__current-label">Current icon</span>
                                </div>
                            )}

                            {/* Drop zone */}
                            <div
                                className={`uip-upload__dropzone ${dragOver ? 'uip-upload__dropzone--active' : ''} ${uploading ? 'uip-upload__dropzone--uploading' : ''}`}
                                onDrop={handleDrop}
                                onDragOver={handleDragOver}
                                onDragLeave={handleDragLeave}
                                onClick={() => !uploading && fileInputRef.current?.click()}
                            >
                                {uploading ? (
                                    <>
                                        <Loader2 size={24} className="uip-upload__spinner" />
                                        <span className="uip-upload__text">Uploading...</span>
                                    </>
                                ) : (
                                    <>
                                        <UploadIcon size={24} className="uip-upload__icon" />
                                        <span className="uip-upload__text">
                                            Drop an image here or <strong>click to browse</strong>
                                        </span>
                                        <span className="uip-upload__hint">
                                            PNG, JPG, WebP, GIF, SVG · Max 2MB
                                        </span>
                                    </>
                                )}
                            </div>

                            {uploadError && (
                                <div className="uip-upload__error">
                                    {uploadError}
                                </div>
                            )}

                            {/* Tip */}
                            <div className="uip-upload__tip">
                                <ImageIcon size={12} />
                                <span>Images are resized to 256×256px for optimal performance</span>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export default memo(UniversalIconPicker);
